import { useContext, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import {
  SEARCH_LOKAREF_CITIES,
  SEARCH_LOKAREF_HOUSE_NUMBERS,
  SEARCH_LOKAREF_STREETS,
} from 'Queries/Queries';
import { ErrorHandlingContext } from 'context/ErrorHandling';

import { PUBLIC_NO_AUTH_CONFIG } from 'components/ApolloComponent/Apollo';

const isAbortError = (error) =>
  error?.name === 'AbortError' ||
  error?.networkError?.name === 'AbortError' ||
  error?.networkError?.message === 'The user aborted a request.' ||
  error?.message === 'signal is aborted without reason';

export const useLokaRef = () => {
  const { setShowError } = useContext(ErrorHandlingContext);

  const cityAbortRef = useRef(null);
  const streetAbortRef = useRef(null);
  const houseNumberAbortRef = useRef(null);

  const [searchLokaRefCitiesQuery, { loading: isCitySearchLoading }] = useLazyQuery(
    SEARCH_LOKAREF_CITIES,
    {
      onError: (error) => {
        if (isAbortError(error)) return;
        console.error('useLokaRef error:', error);
        setShowError((prev) => !prev && true);
      },
    }
  );

  const [searchLokaRefStreetsQuery, { loading: isStreetSearchLoading }] = useLazyQuery(
    SEARCH_LOKAREF_STREETS,
    {
      onError: (error) => {
        if (isAbortError(error)) return;
        console.error('useLokaRef error:', error);
        setShowError((prev) => !prev && true);
      },
    }
  );

  const [searchLokaRefHouseNumbersQuery, { loading: isHouseNumberSearchLoading }] = useLazyQuery(
    SEARCH_LOKAREF_HOUSE_NUMBERS,
    {
      onError: (error) => {
        if (isAbortError(error)) return;
        console.error('useLokaRef error:', error);
        setShowError((prev) => !prev && true);
      },
    }
  );
  const defaultOptions = [
    { title: 'test1', value: 'test1' },
    { title: 'test2', value: 'test2' },
  ];
  const [lokaRefOptions, setLokaRefOptions] = useState({
    houseNumberOptions: [],
    streetOptions: [],
    postalCityOptions: [],
    districtOptions: [],
  });

  const searchLokaRefStreets = async (input) => {
    streetAbortRef.current?.abort();
    const controller = new AbortController();
    streetAbortRef.current = controller;

    try {
      const { data } = await searchLokaRefStreetsQuery({
        variables: { input },
        context: { ...PUBLIC_NO_AUTH_CONFIG.context, fetchOptions: { signal: controller.signal } },
      });
      const lokaRefStreetComboboxOptions = data?.searchLokaRefStreets?.flatMap((street) =>
        street.districts?.length
          ? street.districts.map((district) => ({
              title: `${street.name} (${district.district}, ${district.postcode})`,
              value: `${street.streetId}_${district.districtId}_${district.postcode}`,
              streetId: street.streetId,
              streetName: street.name,
              districtId: district.districtId,
              districtName: district.district,
            }))
          : [
              {
                title: street.name,
                value: String(street.streetId),
                streetId: street.streetId,
                streetName: street.name,
                districtId: null,
                districtName: null,
              },
            ]
      );
      setLokaRefOptions((prev) => ({ ...prev, streetOptions: lokaRefStreetComboboxOptions }));
    } catch (e) {
      if (!isAbortError(e)) throw e;
    }
  };

  const searchLokaRefCities = async (input) => {
    cityAbortRef.current?.abort();
    const controller = new AbortController();
    cityAbortRef.current = controller;

    try {
      const { data } = await searchLokaRefCitiesQuery({
        variables: { input },
        context: { ...PUBLIC_NO_AUTH_CONFIG.context, fetchOptions: { signal: controller.signal } },
      });
      const lokaRefCitiesComboboxOptions = data?.searchLokaRefCities?.flatMap((city) => {
        if (!input.postcode && city.postcodes?.length) {
          return city.postcodes.map((postcodeObj) => ({
            title: `${postcodeObj.postcode} - ${city.name}`,
            value: `${city.cityId}_${postcodeObj.postcode}`,
            cityId: city.cityId,
            postcode: postcodeObj.postcode,
            cityName: city.name,
          }));
        }
        return [
          {
            title: `${input.postcode} - ${city.name}`,
            value: `${city.cityId}_${input.postcode}`,
            cityId: city.cityId,
            postcode: input.postcode,
            cityName: city.name,
          },
        ];
      });
      setLokaRefOptions((prev) => ({ ...prev, postalCityOptions: lokaRefCitiesComboboxOptions }));
    } catch (e) {
      if (!isAbortError(e)) throw e;
    }
  };

  const searchLokaRefHouseNumbers = async (input) => {
    houseNumberAbortRef.current?.abort();
    const controller = new AbortController();
    houseNumberAbortRef.current = controller;

    try {
      const { data } = await searchLokaRefHouseNumbersQuery({
        variables: { input },
        context: { ...PUBLIC_NO_AUTH_CONFIG.context, fetchOptions: { signal: controller.signal } },
      });
      const lokaRefHouseNumbersComboboxOptions = data?.searchLokaRefHouseNumbers?.map(
        (houseNumber) => ({
          title: String(houseNumber.number),
          value: houseNumber.housenrId,
          houseNumber: String(houseNumber.number),
        })
      );
      setLokaRefOptions((prev) => ({
        ...prev,
        houseNumberOptions: lokaRefHouseNumbersComboboxOptions,
      }));
    } catch (e) {
      if (!isAbortError(e)) throw e;
    }
  };

  const resolveAddressIds = async ({ postalCode, city, street, district, country = 'DE' }) => {
    const { data: cityData } = await searchLokaRefCitiesQuery({
      variables: { input: { postcode: postalCode, country, city: '' } },
      context: PUBLIC_NO_AUTH_CONFIG.context,
    });
    const cities = cityData?.searchLokaRefCities ?? [];
    const matchedCity = cities.find(
      (c) => c.name.trim().toLowerCase() === city.trim().toLowerCase()
    );
    if (!matchedCity) return null;

    const matchedPostcode =
      matchedCity.postcodes?.find((p) => p.postcode === postalCode)?.postcode ?? postalCode;

    const { data: streetData } = await searchLokaRefStreetsQuery({
      variables: {
        input: { street, country, cityId: matchedCity.cityId, postcode: matchedPostcode },
      },
      context: PUBLIC_NO_AUTH_CONFIG.context,
    });
    const streets = streetData?.searchLokaRefStreets ?? [];

    let matchedStreetId = null;
    let matchedStreetName = null;
    let matchedDistrictId = null;
    let matchedDistrictName = null;

    for (const s of streets) {
      if (s.name.trim().toLowerCase() === street.trim().toLowerCase()) {
        if (district && s.districts?.length) {
          const matched = s.districts.find(
            (d) => d.district.trim().toLowerCase() === district.trim().toLowerCase()
          );
          if (matched) {
            matchedStreetId = s.streetId;
            matchedStreetName = s.name;
            matchedDistrictId = matched.districtId;
            matchedDistrictName = matched.district;
            break;
          }
        } else {
          matchedStreetId = s.streetId;
          matchedStreetName = s.name;
          break;
        }
      }
    }

    if (!matchedStreetId) return null;

    return {
      cityId: matchedCity.cityId,
      postcode: matchedPostcode,
      cityName: matchedCity.name,
      streetId: matchedStreetId,
      streetName: matchedStreetName,
      districtId: matchedDistrictId ?? null,
      districtName: matchedDistrictName ?? null,
    };
  };

  const clearLokaRefOptions = ({
    cities = false,
    districts = false,
    streets = false,
    houseNumbers = false,
  } = {}) => {
    setLokaRefOptions((prev) => ({
      ...prev,
      ...(cities && { postalCityOptions: [] }),
      ...(districts && { districtOptions: [] }),
      ...(streets && { streetOptions: [] }),
      ...(houseNumbers && { houseNumberOptions: [] }),
    }));
  };

  const abortAll = () => {
    cityAbortRef.current?.abort();
    streetAbortRef.current?.abort();
    houseNumberAbortRef.current?.abort();
  };

  return {
    lokaRefOptions,
    lokaRefLoadingObjects: {
      isCitySearchLoading,
      isStreetSearchLoading,
      isHouseNumberSearchLoading,
    },
    lokaRefSearchFunctions: {
      searchLokaRefCities,
      searchLokaRefStreets,
      searchLokaRefHouseNumbers,
      resolveAddressIds,
    },
    clearLokaRefOptions,
    abortAll,
  };
};
